import React, { useEffect, useState } from 'react';
import CreateAccountPrompt from '../components/CreateAccountPrompt';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Clock, ArrowRight, ArrowLeft, Check, Sparkles, Video, CalendarPlus,
  Star, Mail, RotateCcw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Chrome-less, brand-themed scheduler designed to be embedded via iframe on external sites.
 * URL: /embed/schedule?brand=<slug>&service=<service_id?>
 * - No site header/footer
 * - Pulls theme + services + intake questions from /api/booking-brands/<slug>
 * - Posts to /api/brand-book-slot
 */
export default function EmbedSchedulePage({ brandSlug: brandSlugProp, serviceId: forcedServiceIdProp, inPortal = false } = {}) {
  const [params] = useSearchParams();
  const brandSlug = brandSlugProp || params.get('brand') || 'dca';
  const forcedServiceId = forcedServiceIdProp || params.get('service');
  const paidToken = params.get('paid_session');
  const stripeSessionId = params.get('session_id');

  const [brand, setBrand] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [step, setStep] = useState(0); // 0=service-select, 1=date, 2=time, 3=details, 4=intake, 5=confirmed
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [intake, setIntake] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [prepaid, setPrepaid] = useState(false);
  const [verifying, setVerifying] = useState(!!paidToken);
  const [reqDate, setReqDate] = useState('');
  const [reqTime, setReqTime] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    document.body.style.background = 'transparent';
    axios.get(`${API_URL}/api/booking-brands/${brandSlug}`)
      .then((r) => {
        setBrand(r.data);
        if (paidToken) return; // returning from payment - handled by the effect below
        if (r.data.services?.length === 1) {
          const s = r.data.services[0];
          setSelectedService(s);
          setStep(Number(s.price) > 0 ? 'pay' : 1);
        } else if (forcedServiceId) {
          const s = r.data.services.find((x) => x.service_id === forcedServiceId);
          if (s) { setSelectedService(s); setStep(Number(s.price) > 0 ? 'pay' : 1); }
        }
      })
      .catch(() => toast.error('Brand not found. Please contact support.'));
  }, [brandSlug, forcedServiceId, paidToken]);

  // Returning from Stripe: verify payment, then prefill + jump to date selection.
  useEffect(() => {
    if (!brand || !paidToken) return;
    let cancelled = false;
    (async () => {
      setVerifying(true);
      if (stripeSessionId) {
        for (let i = 0; i < 6; i++) {
          try {
            const s = await axios.get(`${API_URL}/api/payments/status/${stripeSessionId}`);
            if (s.data.payment_status === 'paid') break;
          } catch { /* keep polling */ }
          await new Promise((res) => setTimeout(res, 1500));
        }
      }
      try {
        const r = await axios.get(`${API_URL}/api/paid-session/${paidToken}`);
        if (cancelled) return;
        if (r.data.used) { toast.error('This payment was already used to book a session.'); return; }
        if (!r.data.paid) { toast.error('We could not confirm your payment yet. Please refresh in a moment.'); return; }
        const svc = brand.services?.find((x) => x.service_id === r.data.service_id);
        if (svc) setSelectedService(svc);
        setForm({ name: r.data.name || '', email: r.data.email || '', phone: r.data.phone || '', notes: r.data.notes || '' });
        setIntake(r.data.intake_responses || {});
        setPrepaid(true);
        setStep(1);
      } catch {
        if (!cancelled) toast.error('Could not load your paid session.');
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brand, paidToken, stripeSessionId]);

  const initiatePayment = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (!intakeValid()) { toast.error('Please answer all required questions before paying.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/paid-session/checkout`, {
        brand_slug: brand.slug,
        service_id: selectedService.service_id,
        name: form.name, email: form.email, phone: form.phone, notes: form.notes,
        intake_responses: intake,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout');
      setSubmitting(false);
    }
  };

  if (!brand) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-transparent">
        <div className="w-10 h-10 border-4 border-[#94A3B8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (verifying) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center bg-transparent gap-4" data-testid="embed-verifying-payment">
        <div className="w-10 h-10 border-4 border-[#94A3B8] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm text-[#475569] font-semibold">Confirming your payment…</p>
      </div>
    );
  }

  const theme = {
    primary: brand.primary_color || '#1D4ED8',
    accent: brand.accent_color || '#F97316',
    bg: brand.bg_color || '#0F172A',
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth, year, month };
  };

  const handleDateClick = async (day) => {
    const { year, month } = getDaysInMonth(calendarMonth);
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API_URL}/api/booking-slots/${dateStr}`);
      setSlots(res.data.slots || []);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const formatTime = (t) => {
    const [h, m] = t.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  };

  const intakeValid = () => {
    if (selectedService?.skip_intake) return true;
    return (brand.intake_questions || []).every((q) => {
      if (!q.required) return true;
      const v = (intake[q.key] || '').trim();
      if (q.type === 'select') return v.length > 0;
      return v.length >= (q.min_len || 10);
    });
  };

  const handleConfirm = async () => {
    if (!intakeValid()) {
      toast.error('Please answer all required questions before confirming.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/brand-book-slot`, {
        brand_slug: brand.slug,
        service_id: selectedService.service_id,
        date: selectedDate,
        time: selectedSlot,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        intake_responses: intake,
        paid_session_token: prepaid ? paidToken : undefined,
      });
      setConfirmed({
        date: selectedDate, time: selectedSlot,
        booking_id: res.data.booking_id,
        zoom_join_url: res.data.zoom_join_url,
        zoom_password: res.data.zoom_password,
        manage_token: res.data.manage_token,
        manage_url: res.data.manage_url,
      });
      setStep(5);
      toast.success('Session confirmed!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to confirm booking');
    } finally {
      setSubmitting(false);
    }
  };

  const submitTimeRequest = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    if (!reqDate || !reqTime) { toast.error('Please choose your preferred date and time'); return; }
    if (!intakeValid()) { toast.error('Please answer all required questions'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/brand-time-request`, {
        brand_slug: brand.slug,
        service_id: selectedService.service_id,
        preferred_date: reqDate,
        preferred_time: reqTime,
        name: form.name, email: form.email, phone: form.phone,
        intake_responses: intake, notes: form.notes,
      });
      setRequestSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send your request');
    } finally {
      setSubmitting(false);
    }
  };

  const tomorrowStr = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const { firstDay, daysInMonth, year, month } = getDaysInMonth(calendarMonth);
  const btnPrimary = {
    background: theme.primary, color: '#fff',
    padding: '10px 20px', borderRadius: '8px', fontWeight: 700,
    fontSize: '14px', border: 'none', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    width: '100%',
  };
  const btnAccent = { ...btnPrimary, background: theme.accent };

  const stepLabels = [
    { n: 1, l: 'Date' }, { n: 2, l: 'Time' }, { n: 3, l: 'Details' }, ...(selectedService?.skip_intake ? [] : [{ n: 4, l: 'Intake' }]),
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA' }} data-testid="embed-schedule-page">
      {/* Brand Hero */}
      <div style={{ background: theme.bg, color: '#fff' }} className="px-5 py-8 sm:px-8 sm:py-10 text-center" data-testid="embed-brand-hero">
        {brand.logo_url && (
          <img src={brand.logo_url} alt={brand.name} className="h-12 mx-auto mb-3 object-contain" />
        )}
        <div className="text-[11px] font-bold tracking-[2px] uppercase mb-2" style={{ color: theme.accent }}>{brand.name}</div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="embed-brand-title">{brand.tagline || brand.name}</h1>
        {selectedService && (
          <div className="text-sm" style={{ color: '#CBD5E1' }}>
            <Sparkles size={14} className="inline mr-1" style={{ color: theme.accent }} />
            {selectedService.name} · {selectedService.duration_min} min
            {selectedService.price > 0 && ` · $${selectedService.price}`}
          </div>
        )}
      </div>

      <div className="px-4 sm:px-8 py-8">
        {/* Step 0: Service Selection (if multiple services) */}
        {step === 0 && brand.services?.length > 1 && (
          <div className="max-w-2xl mx-auto" data-testid="embed-step-service">
            <h2 className="text-xl font-bold text-[#0A0F1C] mb-4 text-center">Choose your session</h2>
            <div className="space-y-3">
              {brand.services.map((s) => (
                <button
                  key={s.service_id}
                  onClick={() => { setSelectedService(s); setStep(Number(s.price) > 0 ? 'pay' : 1); }}
                  className="w-full bg-white border-2 rounded-xl p-5 text-left hover:shadow-lg transition-all"
                  style={{ borderColor: s.is_featured ? theme.accent : '#E2E8F0' }}
                  data-testid={`embed-service-${s.service_id}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="font-bold text-[#0A0F1C] text-lg">{s.name}</div>
                    <div className="text-lg font-bold" style={{ color: s.price === 0 ? theme.accent : theme.primary }}>
                      {s.member_price && <span className="text-xs line-through text-[#94A3B8] mr-1.5" data-testid={`embed-regular-price-${s.service_id}`}>${s.regular_price}</span>}
                      {s.price === 0 ? (s.members_only ? 'INCLUDED' : 'FREE') : `$${s.price}`}
                    </div>
                  </div>
                  <div className="text-xs text-[#475569] mb-2">{s.duration_min} min</div>
                  <p className="text-sm text-[#475569] mb-3">{s.description}</p>
                  <div className="text-sm font-bold inline-flex items-center gap-1" style={{ color: theme.primary }}>
                    {s.cta_text || 'Book Session'} <ArrowRight size={14} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Pay step: collect details + intake, then Stripe checkout (paid services) */}
        {step === 'pay' && (
          <div className="max-w-2xl mx-auto bg-white p-6 sm:p-8 border border-[#E2E8F0] rounded-2xl shadow-sm" data-testid="embed-step-pay">
            {brand.services?.length > 1 && (
              <button onClick={() => { setSelectedService(null); setStep(0); }} className="text-xs font-semibold mb-3 inline-flex items-center gap-1 hover:underline" style={{ color: theme.primary }}>
                <ArrowLeft size={12} /> Change session
              </button>
            )}
            <div className="p-4 rounded-xl mb-5 flex items-center justify-between" style={{ background: theme.primary + '10' }}>
              <div>
                <div className="font-bold text-[#0A0F1C]">{selectedService?.name}</div>
                <div className="text-xs text-[#475569]">{selectedService?.duration_min} min · Pay now, then pick your time</div>
              </div>
              <div className="text-2xl font-extrabold" style={{ color: theme.primary }}>${selectedService?.price}</div>
            </div>
            <div className="space-y-3 mb-5">
              <input type="text" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-pay-name" />
              <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-pay-email" />
              <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-pay-phone" />
            </div>
            {(brand.intake_questions || []).length > 0 && (
              <div className="space-y-4 mb-5">
                <div className="text-sm font-bold text-[#0A0F1C] flex items-center gap-2"><Sparkles size={14} style={{ color: theme.accent }} /> A few quick questions</div>
                {(brand.intake_questions || []).map((q, idx) => (
                  <div key={q.key} data-testid={`embed-pay-intake-${idx}`}>
                    <label className="block text-sm font-semibold text-[#0A0F1C] mb-2">{q.label}{q.required && <span style={{ color: theme.accent }}> *</span>}</label>
                    {q.type === 'select' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {q.options?.map((opt) => (
                          <button key={opt} type="button" onClick={() => setIntake({ ...intake, [q.key]: opt })} className="text-left text-sm py-2 px-3 rounded-lg border transition-all" style={intake[q.key] === opt ? { background: theme.primary + '15', borderColor: theme.primary, color: theme.primary, fontWeight: 600 } : { background: '#fff', borderColor: '#E2E8F0', color: '#475569' }}>{opt}</button>
                        ))}
                      </div>
                    ) : (
                      <>
                        <textarea placeholder={q.placeholder} value={intake[q.key] || ''} onChange={(e) => setIntake({ ...intake, [q.key]: e.target.value })} rows={2} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" />
                        <p className="text-xs text-[#94A3B8] mt-1">Please write 1–2 sentences (at least {q.min_len || 10} characters).</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
            <button onClick={initiatePayment} disabled={submitting} style={{ ...btnPrimary, opacity: submitting ? 0.6 : 1 }} data-testid="embed-pay-btn">
              {submitting ? 'Opening secure checkout…' : `Pay $${selectedService?.price} & Continue`}
            </button>
            <p className="text-[11px] text-[#94A3B8] text-center mt-3">Secure checkout by Stripe. After payment you'll choose your session time.</p>
          </div>
        )}

        {/* Step indicator */}
        {step >= 1 && step <= 4 && (
          <div className="max-w-2xl mx-auto bg-white border border-[#E2E8F0] rounded-xl px-3 py-3 mb-5 flex items-center justify-center gap-2 sm:gap-3" data-testid="embed-step-indicator">
            {stepLabels.map((s, i) => (
              <React.Fragment key={s.n}>
                {i > 0 && <div className={`w-6 sm:w-10 h-0.5`} style={{ background: step >= s.n ? theme.primary : '#E2E8F0' }}></div>}
                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all" style={{
                    background: step > s.n ? theme.primary : 'transparent',
                    color: step > s.n ? '#fff' : (step === s.n ? theme.primary : '#94A3B8'),
                    border: step === s.n ? `2px solid ${theme.primary}` : (step < s.n ? '2px solid #E2E8F0' : 'none'),
                  }}>
                    {step > s.n ? <Check size={12} /> : s.n}
                  </div>
                  <span className="text-xs font-semibold hidden sm:block" style={{ color: step >= s.n ? theme.primary : '#94A3B8' }}>{s.l}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step 1: Date */}
        {step === 1 && (
          <div className="max-w-md lg:max-w-xl mx-auto bg-white p-6 lg:p-8 border border-[#E2E8F0] rounded-2xl shadow-sm" data-testid="embed-step-date">
            {brand.services?.length > 1 && (
              <button onClick={() => setStep(0)} className="text-xs font-semibold mb-3 inline-flex items-center gap-1 hover:underline" style={{ color: theme.primary }}>
                <ArrowLeft size={12} /> Change session
              </button>
            )}
            <div className="text-center mb-4">
              <h2 className="font-bold text-[#0A0F1C] text-lg">Pick a date</h2>
              <p className="text-xs text-[#94A3B8] mt-0.5">Closed Sundays &amp; 2nd Saturdays · 30-min slots · Bookings start the next day</p>
            </div>
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))} className="p-2 hover:bg-[#F1F5F9] rounded"><ArrowLeft size={18} /></button>
              <h3 className="font-bold text-[#0A0F1C]">{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))} className="p-2 hover:bg-[#F1F5F9] rounded"><ArrowRight size={18} /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(d => <div key={d} className="text-center text-xs lg:text-sm font-medium text-[#94A3B8] py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDay }, (_, i) => <div key={`e-${i}`}></div>)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateObj = new Date(year, month, day);
                const isPast = dateObj < today;
                const isToday = dateObj.getTime() === today.getTime();
                const dow = dateObj.getDay();
                const isSunday = dow === 0;
                const isSecondSat = dow === 6 && day >= 8 && day <= 14;
                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isSelected = selectedDate === dateStr;
                const isDisabled = isPast || isToday || isSunday || isSecondSat;
                return (
                  <button
                    key={day}
                    onClick={() => !isDisabled && handleDateClick(day)}
                    disabled={isDisabled}
                    className={`w-10 h-10 lg:w-14 lg:h-14 rounded-lg text-sm lg:text-lg font-medium transition-all mx-auto flex items-center justify-center ${isDisabled ? 'text-[#D1D5DB] cursor-not-allowed' : 'hover:bg-[#F1F5F9] text-[#0A0F1C]'}`}
                    style={isSelected ? { background: theme.primary, color: '#fff' } : {}}
                    data-testid={`embed-cal-${day}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            {selectedDate && (
              <button onClick={() => setStep(2)} style={btnPrimary} className="mt-4" data-testid="embed-next-time-btn">
                Continue <ArrowRight size={16} />
              </button>
            )}
            <button
              onClick={() => { setSelectedSlot(null); setStep('request'); }}
              className="w-full mt-3 text-xs font-semibold py-2 hover:underline"
              style={{ color: theme.primary }}
              data-testid="embed-request-time-btn"
            >
              Need a time outside business hours? Request a custom time →
            </button>
          </div>
        )}

        {/* Step 2: Time */}
        {step === 2 && (
          <div className="max-w-md lg:max-w-2xl mx-auto bg-white p-6 lg:p-8 border border-[#E2E8F0] rounded-2xl shadow-sm" data-testid="embed-step-time">
            <button onClick={() => setStep(1)} className="text-xs font-semibold mb-3 inline-flex items-center gap-1 hover:underline" style={{ color: theme.primary }}>
              <ArrowLeft size={12} /> Change date
            </button>
            <h3 className="font-bold text-[#0A0F1C] mb-1">Available Times</h3>
            <p className="text-sm text-[#475569] mb-4 flex items-center gap-1"><Calendar size={14} /> {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            {loadingSlots ? (
              <div className="py-8 text-center"><div className="w-8 h-8 border-3 border-[#1D4ED8] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
            ) : slots.length > 0 ? (
              <div className="grid grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
                {slots.map((slot, i) => (
                  <button
                    key={slot.start}
                    onClick={() => setSelectedSlot(slot.start)}
                    className={`py-2 px-3 lg:py-3 text-sm lg:text-base rounded-lg border transition-all font-medium ${selectedSlot === slot.start ? 'text-white' : 'border-[#E2E8F0] text-[#475569] hover:text-[#0A0F1C]'}`}
                    style={selectedSlot === slot.start ? { background: theme.primary, borderColor: theme.primary } : {}}
                    data-testid={`embed-time-${i}`}
                  >
                    {formatTime(slot.start)}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[#475569] text-center py-6">No available slots for this date</p>
            )}
            {selectedSlot && (
              <button onClick={() => prepaid ? handleConfirm() : setStep(3)} disabled={submitting} style={btnPrimary} data-testid="embed-next-details-btn">
                {prepaid ? (submitting ? 'Confirming…' : <>Confirm My Session <Check size={16} /></>) : <>Continue <ArrowRight size={16} /></>}
              </button>
            )}
            <button
              onClick={() => setStep('request')}
              className="w-full mt-3 text-xs font-semibold py-2 hover:underline"
              style={{ color: theme.primary }}
              data-testid="embed-request-time-btn-2"
            >
              Don't see a time that works? Request a custom time →
            </button>
          </div>
        )}

        {/* Step: Request a custom (out-of-hours) time */}
        {step === 'request' && (
          <div className="max-w-2xl mx-auto bg-white p-6 sm:p-8 border border-[#E2E8F0] rounded-2xl shadow-sm" data-testid="embed-step-request">
            {requestSent ? (
              <div className="text-center py-4" data-testid="embed-request-sent">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: theme.primary + '20' }}>
                  <Mail size={26} style={{ color: theme.primary }} />
                </div>
                <h2 className="text-2xl font-bold text-[#0A0F1C] mb-2">Request sent!</h2>
                <p className="text-[#475569] mb-4">Thanks, {form.name?.split(' ')[0] || 'there'}. We've sent your request for <strong>{reqDate} at {reqTime ? formatTime(reqTime) : ''}</strong> to {brand.name}. You'll get an email as soon as it's reviewed — if approved, it'll include your Zoom link.</p>
                <button onClick={() => { setRequestSent(false); setReqDate(''); setReqTime(''); setStep(1); }} style={btnPrimary} data-testid="embed-request-back-cal">Back to calendar</button>
              </div>
            ) : (
              <>
                <button onClick={() => setStep(1)} className="text-xs font-semibold mb-3 inline-flex items-center gap-1 hover:underline" style={{ color: theme.primary }}>
                  <ArrowLeft size={12} /> Back to calendar
                </button>
                <div className="p-3 rounded-lg mb-5" style={{ background: theme.accent + '15' }}>
                  <div className="font-bold text-[#0A0F1C] text-sm mb-1 flex items-center gap-2"><Clock size={14} style={{ color: theme.accent }} /> Request a time outside business hours</div>
                  <div className="text-xs text-[#475569]">Tell us when works best for you. {brand.name} will review your request and email you to approve it — if approved, you'll receive your Zoom link automatically.</div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#0A0F1C] mb-1">Preferred date <span style={{ color: theme.accent }}>*</span></label>
                    <input type="date" min={tomorrowStr} value={reqDate} onChange={(e) => setReqDate(e.target.value)} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-request-date" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#0A0F1C] mb-1">Preferred time <span style={{ color: theme.accent }}>*</span></label>
                    <input type="time" value={reqTime} onChange={(e) => setReqTime(e.target.value)} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-request-time" />
                  </div>
                </div>
                <div className="space-y-3 mb-4">
                  <input type="text" placeholder="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-request-name" />
                  <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-request-email" />
                  <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-request-phone" />
                </div>
                {(brand.intake_questions || []).length > 0 && (
                  <div className="space-y-4 mb-4">
                    <div className="text-sm font-bold text-[#0A0F1C] flex items-center gap-2"><Sparkles size={14} style={{ color: theme.accent }} /> A few quick questions</div>
                    {(brand.intake_questions || []).map((q, idx) => (
                      <div key={q.key} data-testid={`embed-request-intake-${idx}`}>
                        <label className="block text-sm font-semibold text-[#0A0F1C] mb-2">{q.label}{q.required && <span style={{ color: theme.accent }}> *</span>}</label>
                        {q.type === 'select' ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {q.options?.map((opt) => (
                              <button key={opt} type="button" onClick={() => setIntake({ ...intake, [q.key]: opt })} className="text-left text-sm py-2 px-3 rounded-lg border transition-all" style={intake[q.key] === opt ? { background: theme.primary + '15', borderColor: theme.primary, color: theme.primary, fontWeight: 600 } : { background: '#fff', borderColor: '#E2E8F0', color: '#475569' }}>{opt}</button>
                            ))}
                          </div>
                        ) : (
                          <>
                            <textarea placeholder={q.placeholder} value={intake[q.key] || ''} onChange={(e) => setIntake({ ...intake, [q.key]: e.target.value })} rows={2} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" />
                            <p className="text-xs text-[#94A3B8] mt-1">Please write 1–2 sentences (at least {q.min_len || 10} characters).</p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <textarea placeholder="Anything else we should know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8] mb-4" data-testid="embed-request-notes" />
                <button onClick={submitTimeRequest} disabled={submitting || !intakeValid() || !reqDate || !reqTime} style={{ ...btnAccent, opacity: (submitting || !intakeValid() || !reqDate || !reqTime) ? 0.5 : 1 }} data-testid="embed-request-submit">
                  {submitting ? 'Sending request…' : <>Send request <ArrowRight size={16} /></>}
                </button>
                <p className="text-[11px] text-[#94A3B8] text-center mt-3">This does not book a time yet — {brand.name} will confirm by email.</p>
              </>
            )}
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && (
          <div className="max-w-md mx-auto bg-white p-6 border border-[#E2E8F0] rounded-2xl shadow-sm" data-testid="embed-step-details">
            <button onClick={() => setStep(2)} className="text-xs font-semibold mb-3 inline-flex items-center gap-1 hover:underline" style={{ color: theme.primary }}>
              <ArrowLeft size={12} /> Change time
            </button>
            <div className="p-3 rounded-lg mb-4 flex items-center gap-3" style={{ background: theme.primary + '15' }}>
              <Calendar size={18} style={{ color: theme.primary }} />
              <div>
                <div className="text-sm font-bold text-[#0A0F1C]">{new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
                <div className="text-xs text-[#475569]">{formatTime(selectedSlot)} · {selectedService?.duration_min} min</div>
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (!form.name.trim() || !form.email.trim()) { toast.error('Name and email required'); return; } if (selectedService?.skip_intake) { handleConfirm(); return; } setStep(4); }} className="space-y-3">
              <input type="text" placeholder="Full Name" aria-label="Full Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-input-name" />
              <input type="email" placeholder="Email Address" aria-label="Email Address" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-input-email" />
              <input type="tel" placeholder="Phone (optional)" aria-label="Phone number (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" data-testid="embed-input-phone" />
              <textarea placeholder="Anything you'd like to discuss? (optional)" aria-label="Discussion notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]" />
              <button type="submit" style={btnPrimary} data-testid="embed-next-intake-btn" disabled={submitting}>{selectedService?.skip_intake ? (submitting ? 'Booking…' : 'Confirm Booking') : 'Continue to Intake'} <ArrowRight size={16} /></button>
            </form>
          </div>
        )}

        {/* Step 4: Intake */}
        {step === 4 && (
          <div className="max-w-2xl mx-auto bg-white p-6 sm:p-8 border border-[#E2E8F0] rounded-2xl shadow-sm" data-testid="embed-step-intake">
            <button onClick={() => setStep(3)} className="text-xs font-semibold mb-3 inline-flex items-center gap-1 hover:underline" style={{ color: theme.primary }}>
              <ArrowLeft size={12} /> Edit details
            </button>
            <div className="p-3 rounded-lg mb-5" style={{ background: theme.accent + '15' }}>
              <div className="font-bold text-[#0A0F1C] text-sm mb-1 flex items-center gap-2">
                <Sparkles size={14} style={{ color: theme.accent }} />
                Quick Intake - required to confirm
              </div>
              <div className="text-xs text-[#475569]">Your answers help David prepare for your {selectedService?.duration_min}-minute session.</div>
            </div>
            <div className="space-y-4">
              {(brand.intake_questions || []).map((q, idx) => (
                <div key={q.key} data-testid={`embed-intake-q-${idx}`}>
                  <label className="block text-sm font-semibold text-[#0A0F1C] mb-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs mr-2" style={{ background: theme.primary, color: '#fff' }}>{idx + 1}</span>
                    {q.label}{q.required && <span style={{ color: theme.accent }}> *</span>}
                  </label>
                  {q.type === 'select' ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-8">
                      {q.options?.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setIntake({ ...intake, [q.key]: opt })}
                          className="text-left text-sm py-2 px-3 rounded-lg border transition-all"
                          style={intake[q.key] === opt ? { background: theme.primary + '15', borderColor: theme.primary, color: theme.primary, fontWeight: 600 } : { background: '#fff', borderColor: '#E2E8F0', color: '#475569' }}
                          data-testid={`embed-intake-${q.key}-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <>
                      <textarea
                        placeholder={q.placeholder}
                        value={intake[q.key] || ''}
                        onChange={(e) => setIntake({ ...intake, [q.key]: e.target.value })}
                        rows={3}
                        className="w-full ml-0 sm:ml-8 sm:w-[calc(100%-2rem)] px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8]"
                        data-testid={`embed-intake-${q.key}-input`}
                      />
                      <p className="text-xs text-[#94A3B8] mt-1 ml-0 sm:ml-8" data-testid={`embed-intake-${q.key}-hint`}>
                        Please write 1–2 sentences (at least {q.min_len || 10} characters).
                        {q.required && (intake[q.key] || '').trim().length > 0 && (intake[q.key] || '').trim().length < (q.min_len || 10) && (
                          <span style={{ color: theme.accent }}> {(q.min_len || 10) - (intake[q.key] || '').trim().length} more to go.</span>
                        )}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setStep(3)} disabled={submitting} className="flex-1 sm:flex-initial px-5 py-2.5 border border-[#E2E8F0] rounded-lg text-sm font-semibold text-[#475569] hover:bg-[#F8FAFC]">Back</button>
              <button onClick={handleConfirm} disabled={submitting || !intakeValid()} style={{ ...btnAccent, opacity: (!intakeValid() || submitting) ? 0.5 : 1, flex: 1 }} data-testid="embed-confirm-btn">
                {submitting ? 'Confirming...' : (<>Confirm My Session <Check size={16} /></>)}
              </button>
            </div>
            {!intakeValid() && (
              <p className="text-xs text-[#94A3B8] mt-3 text-center">Please answer every required question — write 1–2 sentences in each box to enable the Confirm button.</p>
            )}
          </div>
        )}

        {/* Step 5: Confirmed */}
        {step === 5 && confirmed && (
          <div className="max-w-md mx-auto bg-white p-7 border border-[#E2E8F0] rounded-2xl shadow-sm text-center" data-testid="embed-confirmed">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: theme.primary + '20' }}>
              <Check size={26} style={{ color: theme.primary }} />
            </div>
            <h2 className="text-2xl font-bold text-[#0A0F1C] mb-2">You're booked!</h2>
            <p className="text-[#475569] mb-3">Your {selectedService.name} is confirmed.</p>
            <div className="flex items-start gap-2 text-left p-3 rounded-lg mb-4" style={{ background: theme.accent + '14' }} data-testid="embed-email-notice">
              <Mail size={16} style={{ color: theme.accent }} className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#475569]">A confirmation email is on its way to <strong className="text-[#0A0F1C]">{form.email}</strong> with your Zoom link and a calendar invite. You can <strong>reschedule or cancel</strong> anytime from that email or the button below.</p>
            </div>
            {confirmed.zoom_join_url && (
              <a href={confirmed.zoom_join_url} target="_blank" rel="noopener noreferrer" style={btnPrimary} className="mb-2" data-testid="embed-zoom-join">
                <Video size={16} /> Join Zoom Meeting
              </a>
            )}
            <a href={`${API_URL}/api/bookings/${confirmed.booking_id}/calendar.ics`} className="inline-flex items-center justify-center gap-2 w-full bg-white border border-[#E2E8F0] text-[#0A0F1C] hover:bg-[#F8FAFC] font-bold text-sm px-4 py-2.5 rounded-lg mt-2 mb-2" data-testid="embed-add-calendar">
              <CalendarPlus size={14} /> Add to Calendar
            </a>
            {confirmed.manage_token && (
              <a href={`/embed/manage?token=${confirmed.manage_token}&brand=${brand.slug}`} className="inline-flex items-center justify-center gap-2 w-full bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC] font-semibold text-sm px-4 py-2.5 rounded-lg mb-4" data-testid="embed-manage-booking">
                <RotateCcw size={14} /> Reschedule or Cancel
              </a>
            )}
            <div className="p-3 rounded-lg text-left" style={{ background: theme.primary + '10' }}>
              <div className="flex items-center gap-2 mb-1"><Calendar size={14} style={{ color: theme.primary }} /><span className="text-sm font-semibold">{new Date(confirmed.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span></div>
              <div className="flex items-center gap-2"><Clock size={14} style={{ color: theme.primary }} /><span className="text-sm font-semibold">{formatTime(confirmed.time)}</span></div>
            </div>
            <div className="mt-5 text-left" data-testid="embed-next-steps">
              <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8] mb-2">What happens next</div>
              {[
                ['Check your inbox', 'Your confirmation, Zoom link and calendar invite are on the way.'],
                ['David reviews your answers', 'Your intake responses help him prepare a plan around your goals.'],
                ['Show up ready', 'Join on time with your biggest question. Leave with a clear next step.'],
              ].map(([t, d], i) => (
                <div key={t} className="flex gap-3 mb-2.5">
                  <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center text-white shrink-0" style={{ background: theme.primary }}>{i + 1}</span>
                  <div><div className="text-sm font-semibold text-[#0A0F1C]">{t}</div><div className="text-xs text-[#64748B]">{d}</div></div>
                </div>
              ))}
            </div>
            {inPortal && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <a href="/portal/sessions" style={btnPrimary} data-testid="embed-go-sessions">My Sessions</a>
                <a href="/portal" className="inline-flex items-center justify-center bg-white border border-[#E2E8F0] text-[#0A0F1C] font-bold text-sm px-4 py-2.5 rounded-lg" data-testid="embed-go-dashboard">Dashboard</a>
              </div>
            )}
            <p className="text-xs text-[#94A3B8] mt-4">Confirmation ID: <span className="font-mono">{confirmed.booking_id}</span></p>
            <CreateAccountPrompt name={form.name} email={form.email} />
            <div className="mt-6 flex items-center justify-center gap-1 text-xs text-[#94A3B8]">
              Powered by <span className="font-bold" style={{ color: theme.primary }}>{brand.name}</span> <Star size={10} className="ml-1" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
